import React, { useState, useEffect } from 'react';
import { supabaseAuth } from '../services/supabaseAuth';
import { tournamentService } from '../services/tournamentService';
import AdminLogin from './AdminLogin';
import QuizParser from './QuizParser';
import PacketCreator from './PacketCreator';

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard'); // dashboard | parse | create
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabaseAuth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        loadTournaments();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = async () => {
    const isAuth = await supabaseAuth.isAuthenticated();
    setIsAuthenticated(isAuth);
    setLoading(false);
    
    if (isAuth) {
      loadTournaments();
    }
  };

  const loadTournaments = async () => {
    try {
      const data = await tournamentService.getAllTournaments();
      setTournaments(data);
    } catch (error) {
      console.error('Error loading tournaments:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await supabaseAuth.logout();
      setIsAuthenticated(false);
      setTournaments([]);
      setActiveView('dashboard');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        იტვირთება...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  // Render based on active view
  const renderContent = () => {
    switch (activeView) {
      case 'parse':
        return (
          <div style={styles.viewContainer}>
            <button 
              style={styles.backButton}
              onClick={() => setActiveView('dashboard')}
            >
              ← უკან მთავარ გვერდზე
            </button>
            <QuizParser onSave={() => {
              loadTournaments();
              setActiveView('dashboard');
            }} />
          </div>
        );
      
      case 'create':
        return (
          <div style={styles.viewContainer}>
            <button 
              style={styles.backButton}
              onClick={() => setActiveView('dashboard')}
            >
              ← უკან მთავარ გვერდზე
            </button>
            <PacketCreator onSave={() => {
              loadTournaments();
              setActiveView('dashboard');
            }} />
          </div>
        );
      
      default: // dashboard
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>ადმინისტრატორის პანელი</h1>
          <p style={styles.subtitle}>ტურნირების მართვა</p>
        </div>
        <button onClick={handleLogout} style={styles.logoutButton}>
          გასვლა
        </button>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{tournaments.length}</div>
          <div style={styles.statLabel}>ტურნირები</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {tournaments.reduce((sum, t) => sum + (t.questions_count || 0), 0)}
          </div>
          <div style={styles.statLabel}>სულ კითხვები</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {tournaments.filter(t => t.published).length}
          </div>
          <div style={styles.statLabel}>გამოქვეყნებული</div>
        </div>
      </div>

      {/* Main Action Buttons */}
      <div style={styles.actionsSection}>
        <h2 style={styles.sectionTitle}>რა გსურთ?</h2>
        
        <div style={styles.actionCards}>
          {/* Parse Word File Card */}
          <div 
            style={styles.actionCard}
            onClick={() => setActiveView('parse')}
          >
            <div style={styles.actionIcon}>📄</div>
            <h3 style={styles.actionTitle}>ფაილის დამუშავება</h3>
            <p style={styles.actionDescription}>
              ატვირთეთ Word (.docx) ფაილი და დაამუშავეთ კითხვები ავტომატურად
            </p>
            <button style={styles.actionButton}>
              დაწყება →
            </button>
          </div>

          {/* Create Packet Card */}
          <div 
            style={styles.actionCard}
            onClick={() => setActiveView('create')}
          >
            <div style={styles.actionIcon}>✏️</div>
            <h3 style={styles.actionTitle}>პაკეტის შექმნა</h3>
            <p style={styles.actionDescription}>
              შექმენით ახალი ტურნირი თავიდან კითხვა-კითხვის დამატებით
            </p>
            <button style={styles.actionButton}>
              დაწყება →
            </button>
          </div>
        </div>
      </div>

      {/* Recent Tournaments */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>ბოლო ტურნირები</h2>
        
        {tournaments.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📭</div>
            <p style={styles.emptyText}>ჯერ არ არის ტურნირები</p>
            <p style={styles.emptySubtext}>შექმენით პირველი ტურნირი ზემოთ მოცემული ბარათებით</p>
          </div>
        ) : (
          <div style={styles.tournamentList}>
            {tournaments.slice(0, 10).map(tournament => (
              <div key={tournament.id} style={styles.tournamentItem}>
                <div style={styles.tournamentHeader}>
                  <h3 style={styles.tournamentTitle}>{tournament.title}</h3>
                  {tournament.published && (
                    <span style={styles.publishedBadge}>✓ გამოქვეყნებული</span>
                  )}
                </div>
                <div style={styles.tournamentInfo}>
                  <span>კითხვები: {tournament.questions_count}</span>
                  <span>•</span>
                  <span>{tournament.difficulty}</span>
                  <span>•</span>
                  <span>{new Date(tournament.created_at).toLocaleDateString('ka-GE')}</span>
                </div>
                {tournament.packet_authors && tournament.packet_authors.length > 0 && (
                  <div style={styles.tournamentAuthors}>
                    ავტორები: {tournament.packet_authors.map(a => a.author_name).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div style={styles.container}>
      {renderContent()}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    minHeight: '100vh',
    background: '#f5f7fa',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '18px',
    color: '#666',
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    padding: '30px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  headerTitle: {
    margin: '0 0 5px 0',
    color: '#2d3748',
    fontSize: '28px',
  },
  subtitle: {
    margin: 0,
    color: '#718096',
    fontSize: '14px',
  },
  logoutButton: {
    padding: '10px 20px',
    background: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background 0.2s',
  },
  
  // Stats
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  statValue: {
    fontSize: '3em',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  statLabel: {
    fontSize: '1em',
    opacity: 0.9,
  },
  
  // Actions Section
  actionsSection: {
    marginBottom: '50px',
  },
  sectionTitle: {
    fontSize: '24px',
    color: '#2d3748',
    marginBottom: '20px',
  },
  actionCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px',
  },
  actionCard: {
    background: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'all 0.3s',
    border: '2px solid transparent',
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
      borderColor: '#667eea',
    },
  },
  actionIcon: {
    fontSize: '4em',
    marginBottom: '20px',
  },
  actionTitle: {
    fontSize: '22px',
    color: '#2d3748',
    marginBottom: '15px',
  },
  actionDescription: {
    fontSize: '15px',
    color: '#718096',
    lineHeight: '1.6',
    marginBottom: '25px',
  },
  actionButton: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  
  // Recent Tournaments
  section: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  tournamentList: {
    display: 'grid',
    gap: '15px',
  },
  tournamentItem: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  tournamentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  tournamentTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#2d3748',
  },
  publishedBadge: {
    background: '#4caf50',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  tournamentInfo: {
    display: 'flex',
    gap: '10px',
    color: '#718096',
    fontSize: '14px',
    marginBottom: '8px',
  },
  tournamentAuthors: {
    fontSize: '13px',
    color: '#a0aec0',
  },
  
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '4em',
    marginBottom: '20px',
  },
  emptyText: {
    fontSize: '18px',
    color: '#2d3748',
    marginBottom: '10px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#718096',
  },
  
  // View Container (for parse/create views)
  viewContainer: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  backButton: {
    padding: '10px 20px',
    background: '#e9ecef',
    color: '#495057',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '20px',
    transition: 'background 0.2s',
  },
};

export default AdminPanel;